import $ from 'jquery';
import CurrencyConverter from 'shopify-currency-converter';
import ShopifyVariants from 'shopify-variants';
import Forms from '../Forms';
import AddToCartFlyout from './AddToCartFlyout';
import ProductGallery from './ProductGallery';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$gallery = options.$gallery;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.product = options.product;
    this.useHistory = options.useHistory;

    this.variantHelper = null;

    this.atcCallbacks = options.atcCallbacks;

    // Form
    this.$form = this.$formArea.find('[data-product-form]');
    this.$productAtcButton = this.$formArea.find('[data-product-atc]');
    this.$productVariants = this.$form.find('[data-variants]');
    this.$productOptions = this.$form.find('[data-product-option]');
    this.detailsLink = this.$formArea[0].querySelector('[data-product-details-link]');
    if (this.detailsLink) {
      this.detailsBaseHref = this.detailsLink.getAttribute('href');
    }

    this.variantFields = {
      $price: this.$details.find('[data-price]'),
      $priceMoney: this.$details.find('[data-price] .money'),
      $compareAtPrice: this.$details.find('[data-price-compare-at]'),
      $compareAtPriceMoney: this.$details.find('[data-price-compare-at] .money'),
      $badge: this.$details.find('[data-badge-sales]'),
      $badgeMoneySaved: this.$details.find('[data-price-money-saved]'),
      $badgePercentSaved: this.$details.find('[data-price-percent-saved]'),
      $sku: this.$details.find('[data-product-sku]'),
    };

    this.forms = new Forms(this.$form);
    this.gallery = new ProductGallery(options);

    this._bindEvents();
    this._initOptions();
  }

  unload() {
    this.$form.off(`.product-details-${this.settings.section_id}`);
    this.$window.off(`.product-details-${this.settings.section_id}`);
    this.forms.unload();

    if (this.variantHelper) {
      this.variantHelper.unload();
    }

    this.gallery.unload();
  }

  selectVariant(variantId) {
    if (variantId) {
      this.variantHelper.selectVariant(variantId);
    }
  }

  _bindEvents() {
    this.$window.on(`shopify-variants:switch-variant.product-details-${this.settings.section_id}`, (event, data) => this._switchVariant(event, data));
    this.$form.on(`submit.product-details-${this.settings.section_id}`, event => this._AddToCartFlyout(event));
  }

  _initOptions() {
    if (!this.$productVariants.length) return;

    this.variantHelper = new ShopifyVariants(
      this.product,
      this.$productVariants,
      this.$productOptions,
      { mixedControls: true },
    );
  }

  _switchVariant(event, data) {
    if (data.product !== this.product) return;

    const variant = data.variant;
    const firstLoad = data.firstLoad;

    if (firstLoad) {
      return;
    }

    // Update main select
    this.$productVariants.val(data.variant.id);
    this.gallery.variantChange(variant);

    // Update Variant information
    this._updatePrice({ price: variant.price, compare_at_price: variant.compare_at_price });
    this._updateSKU(variant);
    this._updateBadge(variant);
    this._updateButton(variant);
    this._updateSwatchLabel(variant);
    this._updateFullDetailsLink(variant);

    if (this.useHistory) {
      const url = `${ this.product.handle }?${$.param({ variant: variant.id })}`;
      history.replaceState({}, 'variant', url);
    }
  }

  _updatePrice(price) {
    // Update compare at price
    const hasComparePrice = (
      !!price.compare_at_price && price.compare_at_price > price.price
    );
    this.variantFields.$compareAtPrice.toggleClass('visible', hasComparePrice);
    this.variantFields.$compareAtPriceMoney.text(Shopify.formatMoney(price.compare_at_price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$compareAtPriceMoney[0]);

    // Update price
    this.variantFields.$priceMoney.text(Shopify.formatMoney(price.price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$priceMoney[0]);
  }

  _updateSKU(variant) {
    if (variant.sku === '') {
      this.variantFields.$sku.parent().addClass('product-sku--empty');
    } else {
      this.variantFields.$sku.parent().removeClass('product-sku--empty');
    }

    this.variantFields.$sku.text(variant.sku);
  }

  _updateBadge(variant) {
    const priceSaved = variant.compare_at_price ? variant.compare_at_price - variant.price : 0;
    this.variantFields.$badge.toggle(!!priceSaved);

    if (this.variantFields.$badgeMoneySaved.length) {
      // Update badge if it shows money saved
      this.variantFields.$badgeMoneySaved.text(Shopify.formatMoney(priceSaved, this.settings.money_format));
      CurrencyConverter.update(this.variantFields.$badgeMoneySaved[0]);
    }

    if (this.variantFields.$badgePercentSaved.length) {
      // Update badge if it shows percentiles
      const percentileSaved = Math.round((priceSaved * 100) / variant.compare_at_price);
      this.variantFields.$badgePercentSaved.text(percentileSaved);
    }
  }

  _updateButton(variant) {
    if (variant.available) {
      this.$productAtcButton.text(this.context.product_available);
      this.$productAtcButton
        .removeClass('disabled')
        .prop('disabled', false);
    } else {
      this.$productAtcButton.text(this.context.product_unavailable);
      this.$productAtcButton
        .addClass('disabled')
        .prop('disabled', true);
    }
  }

  _updateSwatchLabel(variant) {
    if (this.settings.enableSwatches) {
      const swatchLabel = this.$form[0].querySelector('[data-option-swatch-value]');
      if (swatchLabel) {
        swatchLabel.innerText = variant.options[parseInt(swatchLabel.dataset.optionSwatchValue, 10)];
      }
    }
  }

  _updateFullDetailsLink(variant) {
    if (this.detailsLink) {
      this.detailsLink.setAttribute('href', `${this.detailsBaseHref}?variant=${variant.id}`);
    }
  }

  _AddToCartFlyout(event) {
    event.preventDefault();

    const formData = this.$form.serializeArray();

    const options = {
      $atcButton: this.$productAtcButton,
      settings: {
        moneyFormat: this.settings.money_format,
        cartRedirection: this.settings.cart_redirection,
      },
    };

    AddToCartFlyout.init(formData, this.product, options, this.atcCallbacks);
  }
}
